// /frontend/src/pages/dashboard/post/youtube/YouTubeOptionsDrawer.js  (v2.0 – modernized UI)
// All logic identical to v1.
import React, { useState } from 'react';

const VIS_OPTS = [
  { key: 'public',   label: 'Public',   icon: 'fas fa-globe' },
  { key: 'unlisted', label: 'Unlisted', icon: 'fas fa-eye-slash' },
  { key: 'private',  label: 'Private',  icon: 'fas fa-lock' },
];

/* Reusable toggle switch */
function Toggle ({ id, checked, onChange, label }) {
  return (
    <label htmlFor={id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', padding:'8px 0', borderBottom:'1px solid #f9fafb' }}>
      <span style={{ fontSize:13, fontWeight:500, color:'#374151' }}>{label}</span>
      <div
        id={id}
        onClick={() => onChange(!checked)}
        style={{
          width:36, height:20, borderRadius:99, position:'relative', flexShrink:0,
          background: checked ? '#2563eb' : '#d1d5db', transition:'background 0.2s', cursor:'pointer',
        }}
      >
        <div style={{
          position:'absolute', top:2, left: checked ? 18 : 2, width:16, height:16,
          borderRadius:'50%', background:'#fff', transition:'left 0.2s',
          boxShadow:'0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </div>
    </label>
  );
}

/* Tag chip input */
function TagInput ({ tags, onAdd, onRemove }) {
  const [tagInput, setTagInput] = useState('');

  const addTag = raw => {
    const tag = raw.trim();
    if (!tag || tag.length > 30 || tags.length >= 15 || tags.includes(tag)) return;
    onAdd(tag);
  };
  const handleKey = e => {
    if (e.key === ',' || e.key === 'Enter') { e.preventDefault(); addTag(tagInput); setTagInput(''); }
  };

  return (
    <div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:6, padding:'8px', border:'1.5px solid #e5e7eb', borderRadius:8, background:'#ffffff', minHeight:42, alignItems:'center' }}
        onFocus={e => e.currentTarget.style.borderColor = '#2563eb'}
        onBlur={e => e.currentTarget.style.borderColor = '#e5e7eb'}
      >
        {tags.map((tg, i) => (
          <span key={i} style={{ display:'flex', alignItems:'center', gap:5, background:'#eff6ff', color:'#2563eb', border:'1px solid #dbeafe', borderRadius:99, padding:'2px 8px 2px 10px', fontSize:12, fontWeight:600 }}>
            {tg}
            <button type='button' onClick={() => onRemove(i)} style={{ background:'transparent', border:'none', cursor:'pointer', color:'#93c5fd', fontSize:14, lineHeight:1, padding:0 }}>×</button>
          </span>
        ))}
        <input
          type='text'
          value={tagInput}
          onChange={e => setTagInput(e.target.value)}
          onKeyDown={handleKey}
          onBlur={() => { addTag(tagInput); setTagInput('') }}
          placeholder={tags.length ? '' : 'Add a tag…'}
          style={{ border:'none', outline:'none', fontSize:12, color:'#111827', flex:'1 0 100px', minWidth:80, background:'transparent' }}
        />
      </div>
      <p style={{ fontSize:11, color:'#9ca3af', marginTop:5, marginBottom:0 }}>
        Press <kbd style={{ background:'#f3f4f6', borderRadius:3, padding:'1px 5px', fontSize:11 }}>,</kbd> or <kbd style={{ background:'#f3f4f6', borderRadius:3, padding:'1px 5px', fontSize:11 }}>Enter</kbd> after each tag (max 15, 30 chars each).
      </p>
    </div>
  );
}

/* Account block */
function AccountBlock ({ acc, updateAccountOption }) {
  const vis         = acc.options?.visibility  ?? 'public';
  const madeForKids = acc.options?.madeForKids ?? false;
  const forceShort  = acc.options?.forceShort  ?? false;
  const tags        = [...(acc.options?.tags ?? [])];
  const title       = acc.options?.title       ?? '';

  const setOpt = patch => updateAccountOption(acc.id, acc.platform, patch);

  return (
    <div style={{ marginTop:12 }}>
      {/* Title */}
      <div style={{ marginBottom:14 }}>
        <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>
          Video Title
        </label>
        <input
          type='text'
          maxLength={100}
          placeholder="Enter a YouTube title…"
          value={title}
          onChange={e => setOpt({ title: e.target.value })}
          style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:'1.5px solid #e5e7eb', fontSize:13, color:'#111827', outline:'none', boxSizing:'border-box' }}
          onFocus={e => e.target.style.borderColor = '#2563eb'}
          onBlur={e => e.target.style.borderColor = '#e5e7eb'}
        />
        <p style={{ fontSize:11, color:'#9ca3af', marginTop:4, marginBottom:0 }}>Defaults to caption if left blank.</p>
      </div>

      {/* Visibility */}
      <div style={{ marginBottom:14 }}>
        <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'#9ca3af', marginBottom:8 }}>Visibility</div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {VIS_OPTS.map(v => {
            const isActive = vis === v.key;
            return (
              <button
                key={v.key}
                type='button'
                onClick={() => setOpt({ visibility: v.key })}
                style={{
                  display:'flex', alignItems:'center', gap:6,
                  padding:'6px 12px', borderRadius:8,
                  border:`1.5px solid ${isActive ? '#FF0000' : '#e5e7eb'}`,
                  background: isActive ? '#fff1f1' : '#ffffff',
                  color: isActive ? '#cc0000' : '#374151',
                  fontSize:12, fontWeight: isActive ? 700 : 500,
                  cursor:'pointer', transition:'all 0.15s',
                }}
              >
                <i className={v.icon} style={{ fontSize:10 }} />
                {v.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Toggles */}
      <div style={{ marginBottom:14 }}>
        <Toggle id={`yt-shorts-${acc.id}`} checked={forceShort} label='Treat as YouTube Short (≤60 s, 9×16)'
          onChange={v => setOpt({ forceShort: v })} />
        <Toggle id={`yt-kids-${acc.id}`} checked={madeForKids} label='Made for kids'
          onChange={v => setOpt({ madeForKids: v })} />
      </div>

      {/* Tags */}
      <div>
        <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:8 }}>Tags</div>
        <TagInput
          tags={tags}
          onAdd={tag => setOpt({ tags: [...tags, tag] })}
          onRemove={idx => { const c=[...tags]; c.splice(idx,1); setOpt({ tags: c }) }}
        />
      </div>
    </div>
  );
}

/* Drawer wrapper */
export default function YouTubeOptionsDrawer ({ youtubeAccounts = [], updateAccountOption }) {
  const [open, setOpen] = useState(true);
  if (!youtubeAccounts.length) return null;

  return (
    <div style={{ background:'#ffffff', border:'1px solid #e5e7eb', borderRadius:12, marginBottom:12, overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
      {/* Header */}
      <button
        type='button'
        onClick={() => setOpen(o => !o)}
        style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', background:'transparent', border:'none', cursor:'pointer' }}
      >
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:26, height:26, borderRadius:'50%', background:'#fff1f1', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <i className='fab fa-youtube' style={{ fontSize:12, color:'#FF0000' }} />
          </div>
          <span style={{ fontSize:13, fontWeight:700, color:'#111827' }}>YouTube Options</span>
        </div>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize:11, color:'#9ca3af' }} />
      </button>

      {/* Body */}
      {open && (
        <div style={{ padding:'4px 16px 16px', borderTop:'1px solid #f3f4f6' }}>
          {youtubeAccounts.map(acc => (
            <div key={acc.id}>
              {youtubeAccounts.length > 1 && (
                <div style={{ fontSize:12, fontWeight:600, color:'#6b7280', marginTop:8, marginBottom:4 }}>{acc.displayName}</div>
              )}
              <AccountBlock acc={acc} updateAccountOption={updateAccountOption} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}